import { randFullName } from '@ngneat/falso';
import {
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
  type INamedDocument,
  type Reffable,
  type WithRef,
} from '@principle-theorem/shared';
import { Timestamp } from '@principle-theorem/shared/firestore';
import { v4 as uuid } from 'uuid';

function getPathId(path: string): string {
  const split = path.split('/');
  return split.pop() ?? uuid();
}

/**
 * Query builder methods a `QueryConstraint` can invoke. `where(...)` and
 * friends from `@principle-theorem/shared` return a constraint whose `_apply`
 * calls the matching method on whatever reference `toQuery` hands it, so a
 * collection stand-in has to answer these to survive the constraint pipeline.
 */
const QUERY_BUILDER_METHODS = [
  'where',
  'limit',
  'orderBy',
  'startAt',
  'startAfter',
  'endBefore',
] as const;

export function MockColRef<T>(path?: string): CollectionReference<T> {
  const defaultPath = `testing/collection/reference/${uuid()}`;
  const colPath = path ?? defaultPath;
  const segments = colPath.split('/');
  const parentDocPath =
    segments.length > 1 ? segments.slice(0, -1).join('/') : undefined;
  const colRef: Record<string, unknown> = {
    type: 'collection',
    id: getPathId(colPath),
    path: colPath,
    // eslint-disable-next-line no-null/no-null
    parent: parentDocPath ? MockDocRef(parentDocPath) : null,
  };

  // Non-enumerable so `toEqual` keeps treating two same-path refs as equal,
  // while `jest.spyOn(col, 'doc')` still finds a real property to replace.
  Object.defineProperty(colRef, 'doc', {
    value: (documentId?: string) =>
      MockDocRef(`${colPath}/${documentId ?? uuid()}`),
    writable: true,
    configurable: true,
    enumerable: false,
  });

  // Self-return keeps the real constraint pipeline running unmodified. These
  // are non-enumerable so `firebase-functions-test` snapshot serialisation,
  // which walks own enumerable keys, never tries to encode a function.
  for (const method of QUERY_BUILDER_METHODS) {
    Object.defineProperty(colRef, method, {
      value: (): Record<string, unknown> => colRef,
      enumerable: false,
      writable: true,
      configurable: true,
    });
  }

  return colRef as unknown as CollectionReference<T>;
}

export function MockDocRef<T extends DocumentData>(
  path?: string
): DocumentReference<T> {
  const defaultPath = `testing/document/reference/${uuid()}`;
  const documentPath = path ?? defaultPath;
  const paths = documentPath.split('/');
  paths.pop();
  const collectionPath = paths.join('/');
  const parentCollection = MockColRef(collectionPath);
  return {
    type: 'document',
    id: getPathId(documentPath),
    path: documentPath,
    collection: parentCollection,
    parent: parentCollection,
  } as unknown as DocumentReference<T>;
}

export function MockNamedDocument<T extends DocumentData>(
  name?: string,
  refPath?: string
): INamedDocument<T> {
  return {
    name: name || `${randFullName()}`,
    ref: MockDocRef<T>(refPath),
  };
}

export function MockReffable<T extends DocumentData>(item: T): Reffable<T> {
  return {
    ...item,
    ref: MockDocRef<T>(),
  };
}

export function MockWithRef<T extends DocumentData>(
  item: T,
  path?: string
): WithRef<T> {
  return {
    ref: MockDocRef<T>(path),
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    ...item,
  };
}
